import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Grid,
  Avatar, Divider, Alert, CircularProgress, MenuItem,
  Select, FormControl, InputLabel, Stack, ListSubheader, IconButton, Popover
} from '@mui/material';
import {
  WhatsApp, ArrowBack, CheckCircle, CalendarMonth, InfoOutlined,
  AccessTime as TimeIcon, ChevronLeft, ChevronRight
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getTeacherPublicProfile } from '../../api/marketplace';
import { createBooking } from '../../api/booking';
import { getCurrentLanguage } from '../../utils/navigation';

const calcEndTime = (start, durationMin) => {
  const [h, m] = start.split(':').map(Number);
  const total = h * 60 + m + durationMin;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const MiniCalendar = ({ value, onChange }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selected = value ? new Date(value + 'T00:00:00') : null;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [view, setView] = useState(() => {
    const d = value ? new Date(value + 'T00:00:00') : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = (e) => { e.stopPropagation(); setView(new Date(year, month - 1, 1)); };
  const nextMonth = (e) => { e.stopPropagation(); setView(new Date(year, month + 1, 1)); };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSelected = (d) => selected && d &&
    selected.getFullYear() === year && selected.getMonth() === month && selected.getDate() === d;

  const isToday = (d) => d &&
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  const isPast = (d) => d && new Date(year, month, d) < today;

  const handleDay = (d) => {
    if (!d || isPast(d)) return;
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    onChange(`${year}-${mm}-${dd}`);
    setAnchorEl(null);
  };

  const fmt = (d) => {
    if (!d) return '';
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US',
      { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.8 }}>
        Preferred Date *
      </Typography>

      {/* Trigger field */}
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.8, height: 56, borderRadius: 2, cursor: 'pointer',
          border: '1px solid',
          borderColor: open ? '#F5A623' : value ? '#F5A623' : 'rgba(0,0,0,0.23)',
          bgcolor: '#fff', transition: 'border-color 0.2s',
          '&:hover': { borderColor: open ? '#F5A623' : 'rgba(0,0,0,0.87)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <CalendarMonth sx={{ color: value ? '#F5A623' : '#bbb', fontSize: 20 }} />
          <Typography variant="body1"
            sx={{ color: value ? '#1a1a1a' : '#9e9e9e', fontSize: '0.97rem' }}>
            {value ? fmt(value) : 'Select a date'}
          </Typography>
        </Box>
        <ChevronRight sx={{
          color: '#bbb', fontSize: 20,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }} />
      </Box>

      {/* Calendar Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { mt: 0.5, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1.5px solid #F5A623', overflow: 'hidden', width: 300 }
        }}
      >
        {/* Month nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, bgcolor: '#FFFBF2', borderBottom: '1px solid #f5f5f5' }}>
          <IconButton size="small" onClick={prevMonth} sx={{ color: '#F5A623' }}>
            <ChevronLeft />
          </IconButton>
          <Typography fontWeight={700} fontSize="0.95rem" color="#1a1a1a">
            {MONTHS[month]} {year}
          </Typography>
          <IconButton size="small" onClick={nextMonth} sx={{ color: '#F5A623' }}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Week labels */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', px: 1.5, pt: 1 }}>
          {WEEK_DAYS.map(d => (
            <Box key={d} sx={{ textAlign: 'center', pb: 0.5 }}>
              <Typography variant="caption" fontWeight={700} color="#bbb" fontSize="0.7rem">{d}</Typography>
            </Box>
          ))}
        </Box>

        {/* Day cells */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', px: 1.5, pb: 1.5, gap: '2px' }}>
          {cells.map((d, i) => {
            const past = isPast(d);
            const sel = isSelected(d);
            const tod = isToday(d);
            return (
              <Box key={i} onClick={() => handleDay(d)} sx={{
                aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%',
                cursor: d && !past ? 'pointer' : 'default',
                bgcolor: sel ? '#F5A623' : 'transparent',
                border: tod && !sel ? '1.5px solid #F5A623' : '1.5px solid transparent',
                transition: 'all 0.15s',
                '&:hover': d && !past && !sel ? { bgcolor: '#FFF3E0' } : {},
              }}>
                {d && (
                  <Typography fontSize="0.82rem" fontWeight={sel ? 700 : tod ? 600 : 400}
                    sx={{ color: sel ? '#fff' : past ? '#ccc' : tod ? '#F5A623' : '#1a1a1a' }}>
                    {d}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>

      {value && (
        <Typography variant="caption" fontWeight={600} sx={{ mt: 0.5, display: 'block', color: '#F5A623' }}>
          Selected: {fmt(value)}
        </Typography>
      )}
    </Box>
  );
};

const getCurrencySymbol = (c) => {
  switch (c) { case 'EUR': return '€'; case 'MAD': return 'د.م.'; case 'GBP': return '£'; default: return '$'; }
};

const PaymentForm = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = getCurrentLanguage();

  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null); // success state holds booking result

  const [form, setForm] = useState({
    fullName: user?.displayName || '',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    lessonType: 'trial',
    paymentMethod: 'bank_transfer',
    date: '',
    time: '',
    message: '',
  });

  useEffect(() => { loadTeacher(); }, [teacherId]);

  const loadTeacher = async () => {
    try {
      const profile = await getTeacherPublicProfile(teacherId);
      setTeacher(profile);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const getPrice = () => {
    if (form.lessonType === 'trial') return teacher?.trialPrice ?? teacher?.trial_price ?? 0;
    return teacher?.pricePerHour ?? teacher?.price_per_hour ?? 0;
  };

  const validate = () => {
    if (!form.fullName.trim()) return 'Full name is required';
    if (!form.email.trim()) return 'Email is required';
    if (!form.phone.trim()) return 'Phone number is required';
    if (!form.date) return 'Please select a preferred date';
    if (!form.time) return 'Please select a preferred time';
    return null;
  };

  const buildWhatsAppMessage = () => {
    const price = getPrice();
    const curr = getCurrencySymbol(teacher?.currency);
    const lessonLabel = form.lessonType === 'trial' ? 'Trial Lesson' : 'Regular Lesson';
    return [
      `🎓 *New Booking Request - Lingoply*`,
      ``,
      `👤 *Student:* ${form.fullName}`,
      `📧 *Email:* ${form.email}`,
      `📱 *Phone:* ${form.phone}`,
      ``,
      `👨‍🏫 *Teacher:* ${teacher?.displayName}`,
      `📚 *Lesson Type:* ${lessonLabel}`,
      `💰 *Price:* ${price === 0 ? 'Free' : `${curr}${price}`}`,
      `📅 *Date:* ${form.date}`,
      `🕐 *Time:* ${form.time}`,
      ``,
      `💳 *Payment:* ${form.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : form.paymentMethod === 'paypal' ? 'PayPal' : 'Cash'}`,
      form.message ? `💬 *Message:* ${form.message}` : '',
      ``,
      `Sent from Lingoply Platform`
    ].filter(Boolean).join('\n');
  };

  const handleConfirmBooking = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSubmitting(true);
    setError('');
    try {
      const price = getPrice();
      const duration = teacher?.lessonDuration || 50;
      const result = await createBooking({
        studentId: user?.id,
        teacherId,
        studentName: form.fullName,
        teacherName: teacher?.displayName,
        studentEmail: user?.email || form.email,
        date: form.date,
        startTime: form.time,
        endTime: calcEndTime(form.time, duration),
        duration,
        price,
        currency: teacher?.currency || 'USD',
        language: teacher?.languages?.[0] || '',
        isTrial: form.lessonType === 'trial',
        paymentMethod: form.paymentMethod,
        notes: form.message,
      });
      setBooking(result);
    } catch (err) {
      setError('Failed to send booking request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getTeacherWhatsApp = () =>
    (teacher?.phoneNumber || teacher?.whatsapp || teacher?.phone || '').replace(/\D/g, '');

  const handleWhatsApp = () => {
    const number = getTeacherWhatsApp();
    if (!number) {
      alert("This teacher hasn't added a phone number yet.");
      return;
    }
    const encoded = encodeURIComponent(buildWhatsAppMessage());
    window.open(`https://wa.me/${number}?text=${encoded}`, '_blank');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 20 }}>
        <CircularProgress sx={{ color: '#F5A623' }} />
      </Box>
    );
  }

  if (!teacher) {
    return (
      <Box sx={{ pt: 12, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto' }}>Teacher not found</Alert>
      </Box>
    );
  }

  const price = getPrice();
  const curr = getCurrencySymbol(teacher.currency);

  // ─── Success Screen ───────────────────────────────────────────────────────
  if (booking) {
    return (
      <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh', pt: 10, pb: 4 }}>
        <Container maxWidth="sm">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(`/${lang}/student/dashboard`)}
            sx={{ mb: 2, color: '#666' }}>
            Back
          </Button>

          <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, border: '1px solid #eee', textAlign: 'center' }}>
            {/* Checkmark */}
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%', bgcolor: '#4CAF50',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3
            }}>
              <CheckCircle sx={{ fontSize: 48, color: '#fff' }} />
            </Box>

            <Typography variant="h5" fontWeight={800} gutterBottom>
              Booking Request Sent!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your booking with{' '}
              <Box component="span" fontWeight={700} color="text.primary">
                {teacher.displayName}
              </Box>{' '}
              has been created. The teacher will review and confirm it shortly.
            </Typography>

            {/* Info box */}
            <Box sx={{
              bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 2,
              p: 2.5, mb: 4, textAlign: 'left'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <InfoOutlined sx={{ color: '#3B82F6', fontSize: 20, mt: 0.2 }} />
                <Typography variant="body2" fontWeight={600} color="#1E40AF">
                  Once confirmed, you'll be able to see:
                </Typography>
              </Box>
              {[
                'The lesson on your Schedule',
                'Live classes from this teacher',
                'Exercises and lessons posted by this teacher',
                'A video call link when lesson time comes',
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3.5, mb: 0.5 }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#3B82F6', flexShrink: 0 }} />
                  <Typography variant="body2" color="#1E3A8A">{item}</Typography>
                </Box>
              ))}
            </Box>

            {/* Action buttons */}
            <Stack spacing={1.5}>
              <Button
                variant="outlined" fullWidth
                onClick={() => navigate(`/${lang}/student/schedule`)}
                sx={{ py: 1.3, fontWeight: 600, borderRadius: 2.5, borderColor: '#F5A623', color: '#F5A623',
                  '&:hover': { bgcolor: '#FFF8F0', borderColor: '#e09500' } }}
              >
                View My Schedule
              </Button>

              <Button
                variant="outlined" fullWidth startIcon={<WhatsApp />}
                onClick={handleWhatsApp}
                disabled={!getTeacherWhatsApp()}
                title={!getTeacherWhatsApp() ? "Teacher hasn't added a phone number" : ''}
                sx={{ py: 1.3, fontWeight: 600, borderRadius: 2.5, borderColor: '#25D366', color: '#25D366',
                  '&:hover': { bgcolor: '#F0FDF4', borderColor: '#1DA851' },
                  '&.Mui-disabled': { borderColor: '#ccc', color: '#ccc' } }}
              >
                Notify on WhatsApp
              </Button>

              <Button
                variant="contained" fullWidth
                onClick={() => navigate(`/${lang}/student/search`)}
                sx={{ py: 1.3, fontWeight: 600, borderRadius: 2.5,
                  bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
              >
                Browse More Teachers
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  // ─── Booking Form ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh', pt: 10, pb: 4 }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2, color: '#666' }}>
          Back
        </Button>

        <Grid container spacing={3}>
          {/* ── Left: Form ── */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 3, sm: 4 }, border: '1px solid #eee', borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Book a Lesson</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Fill in your details to request a booking with {teacher.displayName}.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Grid container spacing={2}>
                {/* Full Name */}
                <Grid item xs={12}>
                  <TextField fullWidth label="Full Name *" value={form.fullName}
                    onChange={handleChange('fullName')} />
                </Grid>

                {/* Email + Phone */}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email *" type="email" value={form.email}
                    onChange={handleChange('email')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone Number *" value={form.phone}
                    onChange={handleChange('phone')} placeholder="+212 6XX XXX XXX" />
                </Grid>

                {/* Lesson Type + Payment Method */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Lesson Type</InputLabel>
                    <Select value={form.lessonType} onChange={handleChange('lessonType')} label="Lesson Type">
                      <MenuItem value="trial">
                        Trial Lesson ({price === 0 && form.lessonType === 'trial' ? 'Free' : `${curr}${teacher.trialPrice ?? teacher.trial_price ?? 0}`})
                      </MenuItem>
                      <MenuItem value="regular">
                        Regular Lesson ({curr}{teacher.pricePerHour ?? teacher.price_per_hour ?? '—'})
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select value={form.paymentMethod} onChange={handleChange('paymentMethod')} label="Payment Method">
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="paypal">PayPal</MenuItem>
                      <MenuItem value="cash">Cash / Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Preferred Date + Preferred Time — same row */}
                <Grid item xs={12} sm={6}>
                  <MiniCalendar
                    value={form.date}
                    onChange={(d) => setForm(prev => ({ ...prev, date: d }))}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.8 }}>
                    Preferred Time *
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={form.time}
                      displayEmpty
                      onChange={(e) => setForm(prev => ({ ...prev, time: e.target.value }))}
                      startAdornment={
                        <TimeIcon sx={{ color: form.time ? '#F5A623' : '#bbb', mr: 1, fontSize: 20 }} />
                      }
                      sx={{
                        height: 56, borderRadius: 2,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F5A623' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F5A623' },
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 280, borderRadius: 2 } } }}
                    >
                      <MenuItem value="" disabled sx={{ color: '#9e9e9e' }}>Select a time slot</MenuItem>
                      <ListSubheader sx={{ color: '#F5A623', fontWeight: 700, bgcolor: '#FFF8EC', lineHeight: '32px' }}>
                        🌅 Morning
                      </ListSubheader>
                      {['08:00','09:00','10:00','11:00'].map(t => (
                        <MenuItem key={t} value={t} sx={{ '&.Mui-selected': { bgcolor: '#FFF8EC', color: '#F5A623', fontWeight: 700 } }}>
                          {t} — {calcEndTime(t, teacher.lessonDuration || 50)}
                        </MenuItem>
                      ))}
                      <ListSubheader sx={{ color: '#F5A623', fontWeight: 700, bgcolor: '#FFF8EC', lineHeight: '32px' }}>
                        ☀️ Afternoon
                      </ListSubheader>
                      {['12:00','13:00','14:00','15:00','16:00'].map(t => (
                        <MenuItem key={t} value={t} sx={{ '&.Mui-selected': { bgcolor: '#FFF8EC', color: '#F5A623', fontWeight: 700 } }}>
                          {t} — {calcEndTime(t, teacher.lessonDuration || 50)}
                        </MenuItem>
                      ))}
                      <ListSubheader sx={{ color: '#F5A623', fontWeight: 700, bgcolor: '#FFF8EC', lineHeight: '32px' }}>
                        🌙 Evening
                      </ListSubheader>
                      {['17:00','18:00','19:00','20:00','21:00'].map(t => (
                        <MenuItem key={t} value={t} sx={{ '&.Mui-selected': { bgcolor: '#FFF8EC', color: '#F5A623', fontWeight: 700 } }}>
                          {t} — {calcEndTime(t, teacher.lessonDuration || 50)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {form.time && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Lesson: {form.time} – {calcEndTime(form.time, teacher.lessonDuration || 50)} · {teacher.lessonDuration || 50} min
                    </Typography>
                  )}
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <TextField fullWidth label="Message (optional)" multiline rows={3}
                    value={form.message} onChange={handleChange('message')}
                    placeholder="Any special requests or schedule preferences..." />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Confirm Booking button */}
              <Button
                fullWidth variant="contained" size="large"
                startIcon={submitting ? null : <CalendarMonth />}
                onClick={handleConfirmBooking}
                disabled={submitting}
                sx={{
                  py: 1.6, mb: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 3,
                  bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' }
                }}
              >
                {submitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Confirm Booking'}
              </Button>

              {/* WhatsApp optional button */}
              <Button
                fullWidth variant="outlined" size="large"
                startIcon={<WhatsApp />}
                onClick={handleWhatsApp}
                disabled={!getTeacherWhatsApp()}
                title={!getTeacherWhatsApp() ? "Teacher hasn't added a phone number" : ''}
                sx={{
                  py: 1.4, fontSize: '0.95rem', fontWeight: 600, borderRadius: 3,
                  borderColor: '#25D366', color: '#25D366',
                  '&.Mui-disabled': { borderColor: '#ccc', color: '#ccc' },
                  '&:hover': { bgcolor: '#F0FDF4', borderColor: '#1DA851' }
                }}
              >
                Also Notify via WhatsApp (optional)
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                Your booking will appear in your schedule and create a link with this teacher. Once confirmed, you'll see their lessons and live classes.
              </Typography>
            </Paper>
          </Grid>

          {/* ── Right: Teacher Summary ── */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, border: '1px solid #eee', borderRadius: 3, position: 'sticky', top: 80 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Avatar src={teacher.photoURL}
                  sx={{ width: 72, height: 72, mx: 'auto', mb: 1, bgcolor: '#F5A623', fontSize: 28 }}>
                  {teacher.displayName?.charAt(0)}
                </Avatar>
                <Typography variant="h6" fontWeight={700}>{teacher.displayName}</Typography>
                <Typography variant="body2" color="text.secondary">{teacher.country}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Lesson</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {form.lessonType === 'trial' ? 'Trial' : 'Regular'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Duration</Typography>
                  <Typography variant="body2" fontWeight={600}>{teacher.lessonDuration || 50} min</Typography>
                </Box>
                {form.date && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Date</Typography>
                    <Typography variant="body2" fontWeight={600}>{form.date}</Typography>
                  </Box>
                )}
                {form.time && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Time</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {form.time} – {calcEndTime(form.time, teacher.lessonDuration || 50)}
                    </Typography>
                  </Box>
                )}
                {teacher.languages?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Language</Typography>
                    <Typography variant="body2" fontWeight={600}>{teacher.languages.join(', ')}</Typography>
                  </Box>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700}>Total</Typography>
                <Typography variant="h5" fontWeight={800} color="#F5A623">
                  {price === 0 ? 'Free' : `${curr}${price}`}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PaymentForm;
